import { Button, Box, Typography, Stack } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router";
import styled from "styled-components/macro";
import DialogWindow from "../other/DialogWindow";
import { useViewerQuery, useViewerDecks } from "../../queries/viewerQuery";
import { LEARN } from "../../root/main/MainRouter";
import { useSetState, useGetState } from "../ReduxProvider";
import { isChromeExtension } from "tools/device";
import { useSetLastImpulse } from "hooks/useSetLastImpulse";
import PriceButton from "root/profile/subscription/PriceButton";
import { SUBSCRIPTION } from "../../root/profile/ProfileRouter.config";
import { useMaxCardsToLearn } from "./useMaxCardsToLearn";
import { useLocation } from "react-router-dom";
import { SlideDownTransition } from "../../styled/transitions/SlideDownTransition";
import MammothWithBubble, { MammothVariant } from "../../root/tutorial/onboarding/MammothWithBubble";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCrown } from "@duocards/pro-solid-svg-icons";

const NOPAYMENTSEXTRA = 10;

const SDialogWindow = styled(DialogWindow)`
  z-index: 1400; // over course view
`;

const GradientButton = styled(Button)`
  background: ${({ theme }) => theme.duo.color.themeGradient};
  color: ${({ theme }) => theme.duo.color.white};
  font-size: ${({ theme }) => theme.duo.fontSize.small};
`;

export const useReachedMaximum = () => {
  const { isSubscriber, noPayments } = useViewerQuery();
  const { deck } = useViewerDecks();
  const TOLEARNMAX = useMaxCardsToLearn();
  return !!deck && deck.stats.unknown >= TOLEARNMAX + (noPayments() ? NOPAYMENTSEXTRA : 0) && !isSubscriber;
};

type Props = {
  showPermanently?: boolean;
  title?: JSX.Element;
  message?: JSX.Element;
  subscribeButtonLabel?: JSX.Element;
  hideSubscribeButton?: boolean;
  hideClose?: boolean;
};

const CardsMaxDialog: React.FC<Props> = ({
  showPermanently = false,
  title,
  message,
  subscribeButtonLabel,
  hideSubscribeButton,
  hideClose = false
}) => {
  const navigate = useNavigate();
  const learnLimit = useGetState("learnLimit");
  const setLearnLimit = useSetState("learnLimit");
  const setLastImpulse = useSetLastImpulse();
  const location = useLocation();
  const prevLocation = useRef<string>("");
  const isVisible = showPermanently || learnLimit;
  const titleText = title || <Trans>20 cards added to your deck</Trans>;
  const messageText = message || <Trans>Learn them now, or upgrade to add more anytime.</Trans>;

  const subscribeButtonLabelText = subscribeButtonLabel || <Trans>Add anytime</Trans>;

  useEffect(() => {
    if (prevLocation.current !== location.pathname) {
      setLearnLimit(false);
    }
    prevLocation.current = location.pathname;
  }, [location, setLearnLimit]);

  useEffect(() => {
    if (isVisible) setLastImpulse("cardsLimit");
  }, [isVisible, setLastImpulse]);

  if (!isVisible) return null;

  const onClose = () => {
    setLearnLimit(false);
    if (showPermanently) navigate(-1);
  };

  const ActionButtons = () => {
    return (
      <Stack direction="column" spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        {!hideSubscribeButton && (
          <GradientButton
            size="large"
            startIcon={<FontAwesomeIcon icon={faCrown} />}
            onClick={() => {
              setLearnLimit(false);
              navigate(SUBSCRIPTION.url());
            }}
          >
            {subscribeButtonLabelText}
          </GradientButton>
        )}

        <Button
          variant="outlined"
          color="primary"
          size="large"
          onClick={() => {
            setLearnLimit(false);
            navigate(LEARN.url());
          }}
        >
          <Trans>learn now</Trans>
        </Button>
      </Stack>
    );
  };

  return (
    <SDialogWindow
      open
      wide
      TransitionComponent={SlideDownTransition}
      TransitionProps={{ timeout: 300 }}
      onClose={hideClose ? undefined : onClose}
    >
      <Box sx={{ py: 3, px: 2 }}>
        <Box sx={{ marginBottom: 3, paddingLeft: 2, paddingRight: 2 }}>
          <Typography variant="h6">{titleText}</Typography>
        </Box>

        <Box sx={{ marginBottom: 10 }}>
          <MammothWithBubble variant={MammothVariant.Large}>
            <Box sx={{ padding: "8px 0" }}>{messageText}</Box>
          </MammothWithBubble>
        </Box>

        {isChromeExtension() ? <PriceButton notInBrowser /> : <ActionButtons />}
      </Box>
    </SDialogWindow>
  );
};

export default CardsMaxDialog;
